// ============================================================================
// utils_deviceAccess.ts
// Lien de connexion « par UUID » d'un device distant.
//
// Un device créé en mode UUID ne saisit rien : il reçoit une URL
// /webrtc/{uuidChaine}{accessToken} où
//   - les 16 premiers caractères sont l'UUID de la chaîne (cf. UuidGenerator
//     côté admin-api, LENGTH = 16) et désignent le microservice à interroger ;
//   - les 5 suivants sont le token qui identifie le device auprès de ce
//     microservice et lui tient lieu de mot de passe.
// ============================================================================

/** Longueur d'un UUID de chaîne (admin-api : UuidGenerator::LENGTH). */
export const CHANNEL_UUID_LENGTH = 16;

/** Longueur du token device (microservice : ACCESS_TOKEN_LENGTH). */
export const DEVICE_ACCESS_TOKEN_LENGTH = 5;

export interface ParsedDeviceAccessUuid {
  /** UUID de la chaîne — toujours renseigné si l'entrée est exploitable. */
  channelUuid: string;
  /** Token device, ou null si l'URL ne porte que l'UUID de la chaîne. */
  accessToken: string | null;
}

/** Alphabet du token — identique à celui du microservice (ACCESS_TOKEN_CHARSET). */
const ACCESS_TOKEN_CHARSET = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';

/**
 * Tire un token device. C'est la régie qui le génère — et non le microservice —
 * pour pouvoir communiquer le lien (par mail) avant de créer le device ; elle
 * vérifie ensuite auprès du microservice qu'il est libre.
 */
export const generateDeviceAccessToken = (): string => {
  const bytes = new Uint8Array(DEVICE_ACCESS_TOKEN_LENGTH);
  crypto.getRandomValues(bytes);
  return Array.from(bytes, b => ACCESS_TOKEN_CHARSET[b % ACCESS_TOKEN_CHARSET.length]).join('');
};

/** Construit le lien composite distribué au porteur du device. */
export const buildDeviceAccessUuid = (channelUuid: string, accessToken: string): string =>
  `${channelUuid}${accessToken}`;

/**
 * Découpe le paramètre d'URL en (UUID chaîne, token device).
 *
 * - 21 caractères alphanumériques (ou 16-5) → lien de connexion directe ;
 * - tout le reste                           → l'URL ne porte que l'UUID de la
 *   chaîne, on retombe sur la page d'authentification. On ne rejette rien :
 *   les liens historiques /webrtc/{uuid} doivent continuer de fonctionner quelle
 *   que soit la longueur réellement en base.
 */
export const parseDeviceAccessUuid = (raw?: string | null): ParsedDeviceAccessUuid | null => {
  const value = (raw ?? '').trim();
  if (!value) return null;

  const separated = value.match(
    new RegExp(`^([A-Za-z0-9]{${CHANNEL_UUID_LENGTH}})-([A-Za-z0-9]{${DEVICE_ACCESS_TOKEN_LENGTH}})$`),
  );
  if (separated) return { channelUuid: separated[1], accessToken: separated[2] };

  if (
    value.length === CHANNEL_UUID_LENGTH + DEVICE_ACCESS_TOKEN_LENGTH
    && /^[A-Za-z0-9]+$/.test(value)
  ) {
    return {
      channelUuid: value.slice(0, CHANNEL_UUID_LENGTH),
      accessToken: value.slice(CHANNEL_UUID_LENGTH),
    };
  }

  return { channelUuid: value, accessToken: null };
};
